import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScienceHeader } from "@/components/ScienceHeader";
import { SmartImage } from "@/components/SmartImage";
import { Skeleton } from "@/components/ui/skeleton";
import { getScienceIcon, SCIENCE_DISCLAIMER } from "@/data/science";
import { fetchScience } from "@/lib/api";
import { ArrowRight } from "lucide-react";

const GROUPS = ["Technology", "Herbal & Marine"];

export default function ScienceIndex() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;
    fetchScience()
      .then((d) => mounted && setArticles(d))
      .catch(() => mounted && setArticles([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const extraGroups = [...new Set(articles.map((a) => a.group))].filter((g) => !GROUPS.includes(g));
  const groupOrder = [...GROUPS, ...extraGroups];

  return (
    <div className="min-h-screen bg-background">
      <ScienceHeader />

      <section className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:py-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-accent">Science ng Ecentra</p>
          <h1 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Ang science sa likod ng bawat infused element
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            Alamin nang mas malalim kung paano gumagana ang mga teknolohiya at natural na sangkap ng Ecentra — kumpleto sa paliwanag at mga sanggunian.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full rounded-2xl" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <p className="text-muted-foreground">Wala pang science articles.</p>
        ) : (
          groupOrder.map((group) => {
            const items = articles.filter((a) => a.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className="mb-10">
                <h2 className="font-display text-xl font-semibold text-foreground">{group}</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((a) => {
                    const Icon = getScienceIcon(a.icon || a.name);
                    return (
                      <Link
                        key={a.slug}
                        to={`/science/${a.slug}`}
                        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-[0_18px_50px_-26px_rgba(15,23,42,0.35)]"
                        data-testid={`science-card-${a.slug}`}
                      >
                        {a.image ? (
                          <div className="aspect-[16/9] w-full overflow-hidden bg-secondary/40" data-testid={`science-card-image-${a.slug}`}>
                            <SmartImage
                              src={a.image}
                              alt={a.name}
                              fit="cover"
                              fallbackLabel={a.name}
                              className="transition-transform duration-300 group-hover:scale-[1.03]"
                            />
                          </div>
                        ) : null}
                        <div className="flex flex-1 flex-col p-5">
                          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary">
                            <Icon className="h-6 w-6" />
                          </span>
                          <h3 className="mt-4 text-lg font-semibold text-foreground group-hover:text-primary">{a.name}</h3>
                          <p className="mt-1 flex-1 text-sm leading-relaxed text-muted-foreground">{a.tagline}</p>
                          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                            Alamin pa <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}

        <p className="mt-4 rounded-lg border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">{SCIENCE_DISCLAIMER}</p>
      </section>
    </div>
  );
}
